import { useCallback, useMemo, useRef, useState } from "react";

import { StaticVariableTag, getVariableHtml } from "@simplyk/common";
import { UseFormReturn } from "react-hook-form";

import { AmplitudeEvents } from "@/constants/amplitude";
import { trpc } from "@/helpers/trpc";
import { useAmplitude } from "@/hooks/amplitude/useAmplitude";
import { useEmailEditorContext } from "@/src/contexts/EmailEditorContext";
import { EmailEditorFormData } from "@/types/email";

type UseThankYouEmailGenerationOptions = {
  form: UseFormReturn<EmailEditorFormData>;
  handleBodyChange: (newBody: string) => void;
  onEmailGenerated?: (body: string) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  emailContentId?: string;
};

export const useThankYouEmailGeneration = ({
  form,
  handleBodyChange,
  onEmailGenerated,
  onLoadingChange,
  emailContentId,
}: UseThankYouEmailGenerationOptions) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const isCancelledRef = useRef(false);
  const isInitialGenerationRef = useRef(true);

  const { logAmplitudeEvent } = useAmplitude();
  const { mutateAsync: generateThankYouEmail } = trpc.generateExtraThankYouEmailContent.useMutation();
  const { saveThankYouEmailToCache } = useEmailEditorContext();

  const defaultGreeting = useMemo(
    () => `Hi, ${getVariableHtml({ text: "First name", tag: StaticVariableTag.FirstName })}`,
    []
  );

  const cancelGeneration = useCallback(
    (transactionId?: string) => {
      if (isCancelledRef.current) {
        return;
      }

      isCancelledRef.current = true;
      setIsGenerating(false);
      onLoadingChange?.(false);
      handleBodyChange(defaultGreeting);

      // Track generation cancellation
      if (transactionId) {
        logAmplitudeEvent(AmplitudeEvents.ExtraThankYouEmailAiGenerationCancelled, {
          transactionId,
          emailContentId,
          wasInitialGeneration: isInitialGenerationRef.current,
        });

        // Save default greeting to cache to prevent re-generation on next open
        saveThankYouEmailToCache(transactionId, defaultGreeting);
      }
    },
    [defaultGreeting, handleBodyChange, onLoadingChange, saveThankYouEmailToCache, logAmplitudeEvent, emailContentId]
  );

  const generateEmail = useCallback(
    async (transactionId: string, length?: "shorter" | "longer" | "regenerate") => {
      isCancelledRef.current = false;
      setIsGenerating(true);
      onLoadingChange?.(true);

      // Track regeneration requests
      if (length) {
        isInitialGenerationRef.current = false;
        logAmplitudeEvent(AmplitudeEvents.ExtraThankYouEmailAiRegenerationRequested, {
          transactionId,
          emailContentId,
          action: length,
        });
      }

      const { data } = await generateThankYouEmail({
        transactionId,
        length,
      });

      if (!isCancelledRef.current && data?.body) {
        handleBodyChange(data.body);
        form.setValue("body", data.body, { shouldDirty: true });
        saveThankYouEmailToCache(transactionId, data.body);
        onEmailGenerated?.(data.body);
      }

      if (!isCancelledRef.current) {
        setIsGenerating(false);
        onLoadingChange?.(false);
      }
    },
    [
      form,
      generateThankYouEmail,
      handleBodyChange,
      onEmailGenerated,
      onLoadingChange,
      saveThankYouEmailToCache,
      logAmplitudeEvent,
      emailContentId,
    ]
  );

  return {
    generateEmail,
    isGenerating,
    cancelGeneration,
  };
};
